import React, { useRef, useState } from "react";
import clsx from "clsx";
import { Send, Paperclip } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useFileUploader } from "../../hooks/useUploadFile";

interface MessageInputProps {
  onSendMessage: (
    _content: string,
    _type: "txt" | "IMAGE" | "VIDEO" | "AUDIO",
    _fileUrl?: string
  ) => Promise<void>;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [messageContent, setMessageContent] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [fileType, setFileType] = useState<"IMAGE" | "VIDEO" | "AUDIO" | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);

  const acceptedTypes = {
    IMAGE: ["image/jpeg", "image/png", "image/gif"],
    VIDEO: ["video/mp4", "video/quicktime"],
    AUDIO: ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4", "audio/x-m4a"],
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileCategory = Object.keys(acceptedTypes).find((category) =>
      acceptedTypes[category as keyof typeof acceptedTypes].includes(file.type)
    ) as "IMAGE" | "VIDEO" | "AUDIO" | undefined;

    if (!fileCategory) {
      toast.error(t("alert.error.invalidFileType"));
      return;
    }
    setMessageContent("");
    setSelectedFile(file);
    setFileType(fileCategory);
    setFilePreview(URL.createObjectURL(file));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setFileType(null);
  };

  const { uploadFile } = useFileUploader({});

  const handleSend = async () => {
    if (!messageContent.trim() && !selectedFile) {
      toast.error(t("alert.error.emptyMessage"));
      return;
    }

    setIsUploading(true);
    try {
      if (messageContent.trim()) {
        await onSendMessage(messageContent.trim(), "txt");
      }

      if (selectedFile && fileType) {
        // const fileUrl = await new Promise<string>((resolve) => {
        //     setTimeout(() => resolve(URL.createObjectURL(selectedFile)), 1000);
        // });
        const url = await uploadFile(selectedFile, fileType);
        await onSendMessage(url || "", fileType);
      }

      setMessageContent("");
      setSelectedFile(null);
      setFilePreview(null);
      setFileType(null);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    } catch (e: any) {
      toast.error(e?.message || t("alert.error.uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  };

  // const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === "Enter") {
  //     e.preventDefault();
  //     e.stopPropagation();
  //     handleSend();
  //   }
  // };

  return (
    <div className="px-2 sm:px-4 py-3 border-t border-gray-200 bg-white w-full">
      {filePreview && fileType && (
        <div className="mb-2 sm:mb-3 flex  justify-end items-center gap-2 flex-wrap">
          {!isUploading && (
            <button
              onClick={handleRemoveFile}
              className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
          {fileType === "IMAGE" && (
            <img
              src={filePreview}
              alt="Preview"
              className="w-24 h-24 sm:w-20 sm:h-20 object-cover rounded-md"
            />
          )}
          {fileType === "VIDEO" && (
            <video
              src={filePreview}
              className="w-24 h-24 sm:w-40 sm:h-40 object-cover rounded-md"
              controls
            />
          )}
          {fileType === "AUDIO" && (
            <audio
              src={filePreview}
              className="w-full max-w-xs sm:max-w-md"
              controls
            />
          )}
        </div>
      )}

      <div className="flex items-center gap-2 w-full flex-wrap sm:flex-nowrap">
        <label className="p-2 rounded-full hover:bg-gray-100 cursor-pointer">
          <Paperclip size={18} className="text-gray-500" />
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept={Object.values(acceptedTypes).flat().join(",")}
            onChange={handleFileChange}
          />
        </label>
        <textarea
          placeholder={t("input.placeholder.messages") || "Type a message..."}
          ref={inputRef}
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              e.stopPropagation();
              handleSend();
              // Reset height after sending message
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.style.height = "40px";
                }
              }, 0);
            }
          }}
          rows={1}
          style={{ minHeight: "40px", maxHeight: "120px", overflowY: "auto" }}
          className="flex-1 px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base resize-none"
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = "auto";
            target.style.height = `${target.scrollHeight}px`;
          }}
          onBlur={(e) => {
            // Reset height when textarea loses focus
            const target = e.target as HTMLTextAreaElement;
            target.style.height = "40px";
          }}
        />
        <button
          onClick={handleSend}
          disabled={(!messageContent.trim() && !selectedFile) || isUploading}
          className={clsx(
            "px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-1 text-sm sm:text-base",
            (!messageContent.trim() && !selectedFile) || isUploading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-orange-500 text-white hover:bg-orange-600"
          )}
        >
          {isUploading ? (
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Send size={14} className="" />
              <span className="">Send</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MessageInput;
